import React, { useEffect, useRef } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { IApplicationState } from '@store'
import { setSelectedRunScope } from '@store/agentSourcedCreators/actions'
import { selectSelectedRunScope, selectSourcingRuns } from '@store/agentSourcedCreators/selectors'
import {
  selectAtRunLimit,
  selectCanStartDiscoveryRun,
  selectSourcingBlock,
} from '@store/discoveryRuns/selectors'
import { queryStringtoObject } from '@utils'
import { ISourcingRun } from '../types'
import { channelCountsSummaryParts } from '../utils/channelCounts'
import { getEffectiveSelectedRun } from '../utils/effectiveSelectedRun'
import { formatImportedAt } from '../utils/formatImportedAt'
import { effectiveSourcingRunStatus } from '../utils/sourcingRunEffectiveStatus'
import { isCompletedWithNoCreators } from '../utils/sourcingRunEmptyResult'
import { computeVibeCheckProgress } from '../utils/sourcingRunProgress'
import { sourcingRunStatusLabel } from '../utils/sourcingRunStatusLabel'
import { NEUTRAL_TONE, sourcingRunStatusTone } from '../utils/sourcingRunStatusTone'
import {
  NewRunTabButton,
  RunRecordBar,
  RunRecordChannelCounts,
  RunRecordDate,
  RunRecordFailedNote,
  RunRecordName,
  RunRecordNote,
  RunRecordProgress,
  RunRecordProgressFill,
  RunRecordProgressTrack,
  RunRecordStatusPill,
  RunTabButton,
  RunTabDot,
  RunTabsRow,
  RunTabsUsage,
  RunTabsWrapper,
} from './RunTabs.styled'

/**
 * Run-scoping control for the vibe-check review views (BRAND-9306), Idea-2
 * layout (BRAND-9515 rework — replaces the RunScopeBar button-strip
 * presentation). Runs are presented as tabs directly on the Vibe Check page
 * — one tab per run (oldest first, newest last, per the Idea-2 prototype)
 * plus a "+ New run" tab — plus a record bar for the selected run
 * underneath.
 *
 * "+ New run" / "Retry run" are link-driven (BRAND-9307): both are enabled
 * iff the campaign currently carries the `start_discovery_run` rel
 * (selectCanStartDiscoveryRun — the same selector DiscoveryRunForm gates its
 * own Start button on), and clicking either calls `onRequestNewRun` to reveal
 * an inline DiscoveryRunForm (pre-filled from the brief's sourcing block —
 * transcript/notes already show attached). This naturally splits BRAND-9307
 * from BRAND-9511's scope without an explicit branch: today the BE only
 * reissues the link after a FAILED first run (failed runs aren't "prior
 * non-failed"), so that's the only case where these affordances light up;
 * once BRAND-9511 extends BE legality to rerunning after a SUCCESSFUL run,
 * the link will simply start appearing there too and this same
 * link-presence check covers it — no FE change needed.
 *
 * Visibility (BRAND-9307 blast-radius rule):
 * - ZERO runs: always hidden (the Vibe Check page renders DiscoveryRunForm's
 *   zero-state instead; see selectIsFirstRunSetupState).
 * - Exactly ONE run: shown ONLY when the campaign carries the `sourcing`
 *   block — i.e. the brief is actually on the new discovery-runs flow
 *   (capture shots 06/09/10 show Run 1's RUNNING/record-bar treatment).
 *   Legacy single-run campaigns (no sourcing block: toggle off, non-staff,
 *   pre-9307 briefs whose metadata still reports one historical run) keep
 *   the original BRAND-9515 hide-when-≤1-run behavior, so the 9307 rollout
 *   changes nothing for them.
 * - TWO+ runs: always shown (unchanged BRAND-9515 behavior).
 *
 * No "All runs" tab (Ted design feedback 2026-07-15, prototype review): the
 * Idea-2 tab strip is `Run 1 | + New run` only. `?sourcing_run=all` stays
 * BE-supported (appendRunScopeParam/AgentSourcedRunScope untouched) but has
 * no UI entry point — a stray `?run=all` deep-link (or a pre-rework
 * bookmark) falls back to the default (latest) scope instead of rendering a
 * dead "All runs" state.
 *
 * Contract is provisional pending BRAND-9510 (BE) — re-verify the
 * `sourcing_runs` shape and the `?run=` deep-link param name once that PR
 * merges. selectedRunScope/sourcingRuns state, the `?sourcing_run=` fetch
 * param, sourcingRunStatusLabel and computeVibeCheckProgress are unchanged
 * contract-level pieces reused as-is from the original RunScopeBar.
 */
interface IProps {
  campaignId: number
  // Called when the user clicks "+ New run" or "Retry run" while enabled —
  // the parent (AgentSourcedCreatorListView) reveals a DiscoveryRunForm in
  // response. Optional: omitted entirely in token mode, where this control
  // never renders.
  onRequestNewRun?: () => void
  // True whenever the parent is currently showing a DiscoveryRunForm that
  // this control opened (via onRequestNewRun) — i.e. the "+ New run"
  // click/Retry click reveal, NOT the separate zero-runs auto-form (RunTabs
  // never renders on zero runs regardless). Ted's design rule, verbatim:
  // "When we click new Run the new run should be highlighted right?" — the
  // open form IS the current selection, so while this is true, "+ New run"
  // renders in the same pressed/selected treatment a run pill gets, and no
  // run pill is pressed. Defaults to false (unchanged behavior) for callers
  // that don't track this (e.g. absent entirely in token mode).
  isDiscoveryFormOpen?: boolean
  // Called (in addition to dispatching setSelectedRunScope) when a run pill
  // is clicked, so the parent can close a currently-open DiscoveryRunForm
  // and hand selection back to the clicked run. Safe to omit/no-op when the
  // form isn't open.
  onRunSelect?: () => void
}

const RunTabs: React.FC<IProps> = ({
  campaignId,
  onRequestNewRun,
  isDiscoveryFormOpen,
  onRunSelect,
}) => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const { search } = useLocation()
  const sourcingRuns = useSelector(selectSourcingRuns)
  const selectedRunScope = useSelector(selectSelectedRunScope)
  // Presence of the campaign's `sourcing` block = this brief is on the new
  // discovery-runs flow — gates the single-run strip (see visibility rules
  // in the component doc above).
  const sourcingBlock = useSelector((state: IApplicationState) =>
    selectSourcingBlock(state, campaignId),
  )
  // Link-driven "+ New run" / "Retry run" gate (BRAND-9307) — see component
  // doc above.
  const canStartNewRun = useSelector((state: IApplicationState) =>
    selectCanStartDiscoveryRun(state, campaignId),
  )
  // BRAND-9511 run limit: "N of M runs used" indicator (only when the BE
  // sends max_sourcing_runs — old payloads omit it and show nothing) plus a
  // legible at-limit state. At the limit the BE also stops issuing
  // start_discovery_run (canStartNewRun goes false anyway) — atRunLimit
  // exists so the disabled "+ New run" can say WHY ("Run limit reached")
  // instead of the generic not-available hint.
  const runsUsed = sourcingBlock?.runs_used
  const maxSourcingRuns = sourcingBlock?.max_sourcing_runs
  const atRunLimit = useSelector((state: IApplicationState) => selectAtRunLimit(state, campaignId))

  // Deep-link support: read `?run=<ordinal>|all` once, on initial mount only —
  // this seeds the redux scope for a shared/reloaded URL. Subsequent
  // in-session navigation is driven entirely by user clicks, not the URL.
  //
  // A numeric ordinal must be validated against the loaded sourcingRuns
  // before it's dispatched (BRAND-9515 review fix): an out-of-range/unknown
  // ordinal previously passed this guard unchecked, leaving selectedRun
  // undefined downstream while isAllRuns stayed false — the record bar then
  // mislabeled that state as "All runs" with an empty 0-of-0 aggregate.
  // sourcingRuns can still be empty on the very first render (they arrive
  // via a separate fetch), so this effect re-runs as sourcingRuns loads and
  // only commits once it has runs to validate against.
  const didInitFromUrl = useRef(false)
  useEffect(() => {
    if (didInitFromUrl.current) return
    const { run } = queryStringtoObject(search) as { run?: string }
    if (run === undefined) {
      didInitFromUrl.current = true
      return
    }
    if (run === 'all') {
      // No "All runs" UI state to land on anymore — treat it exactly like an
      // absent/malformed param and fall back to the default (latest) scope.
      didInitFromUrl.current = true
      return
    }
    if (sourcingRuns.length === 0) return
    didInitFromUrl.current = true
    const parsedOrdinal = Number(run)
    const isKnownOrdinal =
      Number.isInteger(parsedOrdinal) &&
      parsedOrdinal > 0 &&
      sourcingRuns.some((sourcingRun) => sourcingRun.ordinal === parsedOrdinal)
    if (isKnownOrdinal) {
      dispatch(setSelectedRunScope(parsedOrdinal))
    }
    // Malformed values (non-numeric, 0, negative) and unknown ordinals (no
    // matching run) are ignored — the strip falls back to its default
    // (latest run) rather than throwing or mislabeling the state.
  }, [sourcingRuns])

  if (sourcingRuns.length === 0) return null
  // Legacy guard: a single run only renders the strip on discovery-runs
  // briefs (sourcing block present) — see visibility rules above.
  if (sourcingRuns.length === 1 && !sourcingBlock) return null

  // Idea-2 prototype tab order: oldest run first, newest last (the reverse
  // of RunScopeBar's newest-first button order).
  const orderedRuns = [...sourcingRuns].sort((a, b) => a.ordinal - b.ordinal)

  // Shared with AgentSourcedCreatorListView (which needs the same
  // fallback-to-latest resolution to gate its RUNNING/FAILED empty states).
  // Defense in depth (BRAND-9515 review fix): the deep-link parse guard
  // above already stops an out-of-range/unknown ordinal from ever being
  // dispatched, but if selectedRunScope somehow ends up pointing at an
  // ordinal with no matching run (e.g. a run disappearing from a later
  // fetch), this still falls back to the latest run rather than leaving
  // selectedRun undefined.
  const selectedRun = getEffectiveSelectedRun(orderedRuns, selectedRunScope)
  const effectiveOrdinal = selectedRun?.ordinal

  const isRunning = selectedRun?.status === 'importing'
  const isFailed = selectedRun?.status === 'failed'
  const isAnyRunInProgress = sourcingRuns.some((run) => run.status === 'importing')
  // Link-driven (BRAND-9307): disabled while another run is in progress
  // (defense in depth — the BE shouldn't offer the link then either) OR
  // while the campaign doesn't currently carry start_discovery_run OR
  // (BRAND-9511, also defense in depth — the link disappears at the limit
  // anyway) once the run limit is reached.
  const newRunDisabled = isAnyRunInProgress || !canStartNewRun || atRunLimit
  // Disabled-reason priority: a run in progress is the more actionable,
  // temporary reason; the run limit is terminal; anything else gets the
  // generic not-available hint.
  const newRunDisabledTitleId = isAnyRunInProgress
    ? 'creatorSearch.discoveryRun.newRun.disabledWhileRunningTitle'
    : atRunLimit
    ? 'creatorSearch.discoveryRun.newRun.runLimitReachedTitle'
    : 'creatorSearch.vibeCheck.runTabs.newRunTitle'

  const progress = selectedRun
    ? computeVibeCheckProgress([selectedRun.counts])
    : { decided: 0, total: 0 }

  const channelCounts = selectedRun?.channel_counts ?? {}
  const channelParts = channelCountsSummaryParts(channelCounts)
  const channelCountsText = channelParts
    .map(
      (part) =>
        `${part.count} ${part.labelKey ? intl.formatMessage({ id: part.labelKey }) : part.channel}`,
    )
    .join(' · ')

  // BRAND-9582 (QA item 3): a completed run with nobody sourced still
  // reports `ready_for_vibe_check` (see utils/sourcingRunEmptyResult) — use
  // a neutral tone instead of promising creators that aren't there.
  // BRAND-9582 (QA item 14): otherwise tone off the LIVE-derived status
  // (utils/sourcingRunEffectiveStatus), which the raw one goes stale against
  // after a single non-bulk decision/undo.
  const runTone = (run: ISourcingRun): string =>
    isCompletedWithNoCreators(run)
      ? NEUTRAL_TONE
      : sourcingRunStatusTone(effectiveSourcingRunStatus(run))

  const selectedRunHasNoCreators = selectedRun ? isCompletedWithNoCreators(selectedRun) : false
  // BRAND-9582 (QA item 14, MINOR 8): both values are derived together inside
  // one `selectedRun ?` check rather than computed separately, which avoided
  // an `as string` cast (TS couldn't correlate two independent ternaries'
  // nullability) without widening sourcingRunStatusLabel's signature.
  const [selectedEffectiveStatus, selectedStatusLabelKey] = selectedRun
    ? (() => {
        const effectiveStatus = effectiveSourcingRunStatus(selectedRun)
        return [
          effectiveStatus,
          selectedRunHasNoCreators
            ? 'creatorSearch.vibeCheck.runScope.status.noCreatorsSourced'
            : sourcingRunStatusLabel(effectiveStatus),
        ] as const
      })()
    : [null, null]

  return (
    <RunTabsWrapper data-testid="run-tabs">
      <RunTabsRow>
        {orderedRuns.map((run) => {
          // BRAND-9515: an open discovery form takes over the "selected tab"
          // role (see isDiscoveryFormOpen doc above) — no run pill is
          // pressed while it's showing.
          const active = run.ordinal === effectiveOrdinal && !isDiscoveryFormOpen
          return (
            <RunTabButton
              key={run.id}
              type="button"
              $active={active}
              aria-pressed={active}
              onClick={() => {
                dispatch(setSelectedRunScope(run.ordinal))
                onRunSelect?.()
              }}
              data-testid={`run-tab-${run.ordinal}`}
            >
              <FormattedMessage
                id="creatorSearch.vibeCheck.runScope.runLabel"
                values={{ ordinal: run.ordinal }}
              />
              <RunTabDot $color={runTone(run)} />
            </RunTabButton>
          )
        })}
        <NewRunTabButton
          type="button"
          $active={isDiscoveryFormOpen}
          aria-pressed={Boolean(isDiscoveryFormOpen)}
          disabled={newRunDisabled}
          onClick={() => onRequestNewRun?.()}
          title={newRunDisabled ? intl.formatMessage({ id: newRunDisabledTitleId }) : undefined}
          data-testid="run-tab-new"
        >
          <FormattedMessage id="creatorSearch.vibeCheck.runTabs.newRun" />
        </NewRunTabButton>
        {/* BRAND-9511: only rendered when the BE sends max_sourcing_runs —
            old payloads without a cap show nothing here. Styled as exhausted
            at the limit so the terminal state reads at a glance. */}
        {typeof maxSourcingRuns === 'number' && (
          <RunTabsUsage
            $exhausted={atRunLimit}
            data-exhausted={atRunLimit ? 'true' : undefined}
            data-testid="run-tabs-runs-used"
          >
            <FormattedMessage
              id="creatorSearch.discoveryRun.runsUsed"
              values={{ used: runsUsed ?? 0, max: maxSourcingRuns }}
            />
          </RunTabsUsage>
        )}
      </RunTabsRow>

      {/* BRAND-9582 (QA item 5): the record bar belongs to a SELECTED run —
          while the discovery form is open, no run is selected, so it goes
          away too (it used to sit under the form, reading as if it belonged
          to whichever run was last selected — repro: brief 38512). */}
      {!isDiscoveryFormOpen && (
        <RunRecordBar data-testid="run-record-bar">
          <RunRecordName>
            {selectedRun && (
              <FormattedMessage
                id="creatorSearch.vibeCheck.runScope.runLabel"
                values={{ ordinal: selectedRun.ordinal }}
              />
            )}
          </RunRecordName>

          {selectedRun && (
            <RunRecordStatusPill $color={runTone(selectedRun)} data-testid="run-record-status">
              {selectedStatusLabelKey ? (
                <FormattedMessage id={selectedStatusLabelKey} />
              ) : (
                selectedEffectiveStatus
              )}
            </RunRecordStatusPill>
          )}

          {/* While a run is importing or has failed there's nothing to vibe
              check yet — the "X of Y vibe checked" progress text is replaced
              by the RUNNING progress-bar treatment / FAILED copy below. A run
              that completed with nobody sourced (BRAND-9582) has nothing to
              check either — the pill already says so. */}
          {!isRunning && !isFailed && !selectedRunHasNoCreators && (
            <RunRecordProgress data-testid="run-record-progress">
              <FormattedMessage
                id="creatorSearch.vibeCheck.runScope.progress"
                values={{ decided: progress.decided, total: progress.total }}
              />
            </RunRecordProgress>
          )}

          {channelParts.length > 0 && (
            <RunRecordChannelCounts data-testid="run-record-channels">
              {channelCountsText}
            </RunRecordChannelCounts>
          )}

          {selectedRun && (
            <RunRecordDate data-testid="run-record-date">
              {formatImportedAt(selectedRun.imported_at)}
            </RunRecordDate>
          )}

          {/* BRAND-9582 (QA item 4): "Retry run" removed — it duplicated
              "+ New run" (same handler, same link), and "retry" implied
              re-attempting the failed run rather than starting a fresh one. */}

          {isRunning && (
            <>
              <RunRecordProgressTrack data-testid="run-record-progress-bar">
                <RunRecordProgressFill />
              </RunRecordProgressTrack>
              <RunRecordNote data-testid="run-record-running-note">
                <FormattedMessage id="creatorSearch.discoveryRun.record.runningNote" />
              </RunRecordNote>
            </>
          )}

          {isFailed && (
            <RunRecordFailedNote data-testid="run-record-failed-note">
              <FormattedMessage id="creatorSearch.discoveryRun.record.failedNote" />
            </RunRecordFailedNote>
          )}
        </RunRecordBar>
      )}
    </RunTabsWrapper>
  )
}

export { RunTabs }
export default RunTabs
